import React, { forwardRef, useImperativeHandle } from 'react';
import FormBuilder, { FormSchema, FormBuilderRef } from '../../../components/formBuilder/formBuilder';
import { enumToOptions, isEmail } from '../../../utils/misc';
import { useTranslation } from 'react-i18next';
import { OfferQuery, OfferQueryStrategy } from '../../../models/offerQuery';
import { LoadSecurity, SwappableBodyType, TrailerEquipment, TrailerProperty, TrailerType, VehicleType } from '../../../models/vehicle';


export type FormData = Partial<OfferQuery> & {
  startCountry?: string;
  startCity?: string;
  startPostalCode?: string;
  endCountry?: string;
  endCity?: string;
  endPostalCode?: string;
  vehicleTypes?: VehicleType[];
  vehicleBodies?: TrailerType[];
  vehicleBodyProperties?: TrailerProperty[];
  vehicleEquipments?: TrailerEquipment[];
  vehicleLoadSecurities?: LoadSecurity[];
  vehicleSwappableBodyTypes?: SwappableBodyType[];
};

export type FormRef = {
  submit: () => Promise<void>;
}

type Props = {
  formData: FormData;
  actionText?: string,
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  handleSubmit: () => Promise<void>
  onValidationChange?: (hasErrors: boolean) => void;
}

const Form = forwardRef<FormRef, Props>(({
  formData,
  actionText,
  setFormData,
  handleSubmit,
  onValidationChange
}, ref) => {
  const { t } = useTranslation();

  const [formSchema, setFormSchema] = React.useState<FormSchema[]>([
    {
      name: 'startPlace',
      label: t('Start Place'),
      icon: '📍',
      fields: [
        { name: 'startCountry', type: 'country-autocomplete', label: t('Country'), required: true, disable: formData?.id ? true : false, cols: 4, size: 'small' },
        { name: 'startCity', type: 'text', label: t('City'), required: true, disable: formData?.id ? true : false, cols: 4, size: 'small' },
        { name: 'startPostalCode', type: 'text', label: t('Postal Code'), required: false, disable: formData?.id ? true : false, cols: 4, size: 'small' },
      ]
    },
    {
      name: 'endPlace',
      label: t('End Place'),
      icon: '🚚',
      fields: [
        { name: 'endCountry', type: 'country-autocomplete', label: t('Country'), required: true, disable: formData?.id ? true : false, cols: 4, size: 'small' },
        { name: 'endCity', type: 'text', label: t('City'), required: true, disable: formData?.id ? true : false, cols: 4, size: 'small' },
        { name: 'endPostalCode', type: 'text', label: t('Postal Code'), required: false, disable: formData?.id ? true : false, cols: 4, size: 'small' },
      ]
    },
    {
      name: 'vehicleProperties',
      label: t('Vehicle Properties'),
      icon: '🚛',
      fields: [
        { name: 'vehicleTypes', type: 'text', label: t('Types'), required: true, cols: 6, disable: formData?.id ? true : false, size: 'small' },
        { name: 'vehicleBodies', type: 'multiple', label: t('Bodies'), required: false, options: enumToOptions(TrailerType), cols: 6, disable: formData?.id ? true : false, size: 'small' },
        { name: 'vehicleBodyProperties', type: 'multiple', label: t('Body Properties'), required: false, options: enumToOptions(TrailerProperty), cols: 6, disable: formData?.id ? true : false, size: 'small' },
        { name: 'vehicleEquipments', type: 'multiple', label: t('Equipments'), required: false, options: enumToOptions(TrailerEquipment), cols: 6, disable: formData?.id ? true : false, size: 'small' },
        { name: 'vehicleLoadSecurities', type: 'multiple', label: t('Load Securities'), required: false, options: enumToOptions(LoadSecurity), cols: 6, disable: formData?.id ? true : false, size: 'small' },
        { name: 'vehicleSwappableBodyTypes', type: 'multiple', label: t('Swappable Body Types'), required: false, options: enumToOptions(SwappableBodyType), cols: 6, disable: formData?.id ? true : false, size: 'small' },
      ]
    },
    {
      name: 'additionalInfo',
      label: t('Additional Information'),
      icon: '📝',
      fields: [
        { name: 'strategy', type: 'select', label: t('Strategy'), required: true, options: enumToOptions(OfferQueryStrategy), cols: 6, disable: formData?.id ? true : false, size: 'small' },
        { name: 'emailTo', type: 'multiple', label: t('Email to'), required: true, cols: 6, validationRules: [(value) => ((value && !isEmail(value)) ? t('Email required!') : '')], disable: formData?.id ? true : false, size: 'small' },
        { name: 'emailReplyTo', type: 'email', label: t('Reply to'), required: false, disable: formData?.id ? true : false, size: 'small' },
        { name: 'firstSentNotBefore', type: 'date', label: t('First sent not before'), required: false, cols: 6, size: 'small' },
        { name: 'lastSentNotAfter', type: 'date', label: t('Last sent not after'), required: false, cols: 6, disable: formData?.id ? true : false, size: 'small' },
        { name: 'emailBodyGenerationPrompt', type: 'multiline', label: t('Email body generation prompt'), required: false, rows: 3, disable: formData?.id ? true : false, size: 'small' },
        { name: 'active', type: 'switch', label: t('Active'), required: false },
      ]
    }
  ]);

  const formBuilderRef = React.useRef<FormBuilderRef>(null);

  useImperativeHandle(ref, () => ({
    submit: async () => {
      if (formBuilderRef.current) {
        await formBuilderRef.current.submit();
      }
    }
  }));

  return (
    <FormBuilder
      ref={formBuilderRef}
      formSchema={formSchema}
      formData={formData}
      setFormData={setFormData}
      handleSubmit={handleSubmit}
      actionText={actionText}
      onSchemaChange={setFormSchema}
      onValidationChange={onValidationChange}
    />
  );
});

Form.displayName = 'Form';

export default Form;
